import { useState, useCallback } from 'react';
import { AIMessage } from '@/integrations/ai/direct-client';
import { logger } from '@/utils/logger';
import { getSupabaseUrl, getSupabaseKey } from '@/utils/supabaseUtils';

/**
 * Hook for handling AI streaming text generation
 * Uses Edge Functions for secure server-side AI processing
 */
export interface UseAIStreamOptions {
  onStart?: () => void;
  onComplete?: (fullText: string) => void;
  onError?: (error: Error) => void;
  onToken?: (token: string, accumulatedText: string) => void;
}

export interface UseAIStreamResult {
  streamResponse: (messages: AIMessage[], systemPrompt: string, chatbotId?: string) => Promise<void>;
  isStreaming: boolean;
  error: string | null;
  accumulatedText: string;
  resetState: () => void;
}

export function useAIStream(options: UseAIStreamOptions = {}): UseAIStreamResult {
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accumulatedText, setAccumulatedText] = useState('');

  const resetState = useCallback(() => {
    setIsStreaming(false);
    setError(null);
    setAccumulatedText('');
  }, []);

  const streamResponse = useCallback(async (messages: AIMessage[], systemPrompt: string, chatbotId?: string) => {
    try {
      setIsStreaming(true);
      setError(null);
      setAccumulatedText('');

      options.onStart?.();

      // Debug log the messages being sent
      console.log('🔍 AI STREAM DEBUG - Sending to edge function:', {
        chatbotId,
        systemPrompt,
        messageCount: messages.length,
        messages: messages.map(m => ({
          role: m.role,
          contentLength: m.content.length,
          contentPreview: m.content.substring(0, 200) + '...'
        })),
        systemMessage: messages.find(m => m.role === 'system')?.content.substring(0, 500) + '...'
      });

      // Use Edge Function for secure server-side AI processing
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/chat-ai-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getSupabaseKey()}`,
          'apikey': getSupabaseKey()
        },
        body: JSON.stringify({
          chatbot_id: chatbotId,
          messages: messages,
          system_prompt: systemPrompt,
          ai_provider: 'openai'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`AI streaming failed: ${errorText}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body available');
      }

      const decoder = new TextDecoder();
      let fullText = '';

      try {
        // Add timeout for streaming to prevent indefinite waiting
        const timeoutId = setTimeout(() => {
          reader.cancel();
          throw new Error('AI streaming timeout after 30 seconds');
        }, 30000);

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            clearTimeout(timeoutId);
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          fullText += chunk;
          setAccumulatedText(fullText);
          options.onToken?.(chunk, fullText);
        }

        options.onComplete?.(fullText);
      } finally {
        reader.releaseLock();
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown streaming error');
      logger.error('AI', 'Streaming error occurred', { message: error.message, stack: error.stack });
      setError(error.message);
      options.onError?.(error);
    } finally {
      setIsStreaming(false);
    }
  }, [options]);

  return {
    streamResponse,
    isStreaming,
    error,
    accumulatedText,
    resetState
  };
}
